import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { 
  Clock, CheckCircle, ChefHat, UtensilsCrossed, Package,
  ArrowLeft, Phone, RefreshCw, Volume2, VolumeX, BellRing, Printer
} from "lucide-react";
import { cn, formatCurrency, formatTime } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderTracking = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const restaurantSlug = searchParams.get("restaurant");
  const [order, setOrder] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [waiterCalled, setWaiterCalled] = useState(false);
  const previousStatus = useRef(null);
  const audioContext = useRef(null);

  // Only active providers
  const activeProviders = useMemo(
    () => (restaurant?.manual_providers || []).filter(p => p.active !== false),
    [restaurant]
  );

  const handleCallWaiter = async () => {
    if (callingWaiter) return;
    setCallingWaiter(true);
    try {
      await axios.post(`${API}/public/orders/${orderId}/call-waiter`, { reason: null });
      setWaiterCalled(true);
      toast.success("Waiter notified — they're on the way");
      setTimeout(() => setWaiterCalled(false), 60000); // re-enable after 60s
    } catch (e) {
      toast.error("Couldn't reach the waiter. Try again.");
    } finally { setCallingWaiter(false); }
  };

  // Customer-facing receipt print via hidden iframe (mobile + thermal-printer compatible).
  // Always rebuilds the receipt with the CURRENT order total + active providers + USSD codes.
  const handlePrintReceipt = () => {
    if (!order) return;
    try {
      const amt = order.payment_unique_amount || order.total;
      const escape = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;"}[c]));
      const itemRows = (order.items || []).map(it => {
        const extras = (it.extras_details || it.extras || []).map(e => `<div class="ex">+ ${escape(e.name)}${e.price ? ` (${formatCurrency(e.price)})` : ""}</div>`).join("");
        const variant = it.variant_name ? `<div class="ex">[${escape(it.variant_name)}]</div>` : "";
        return `<div class="row"><div class="ln"><span>${it.quantity}× ${escape(it.name)}</span><span>${formatCurrency(it.total)}</span></div>${variant}${extras}</div>`;
      }).join("");

      const providerRows = activeProviders.map(p => {
        // USSD code uses literal "$" placeholder — customer types the amount themselves.
        let ussd = p.merchant_number;
        if (p.ussd_code && p.ussd_code.includes("*")) {
          ussd = p.ussd_code
            .replace(/\{merchant\}/gi, p.merchant_number)
            .replace(/\{amount\}/gi, "$")
            .replace(/\{total\}/gi, "$");
        }
        return `<div class="pay"><div class="pn"><b>${escape(p.name)}</b><span>${escape(p.merchant_number)}</span></div><div class="ussd">${escape(ussd)}</div>${p.instructions ? `<div class="instr">${escape(p.instructions)}</div>` : ""}</div>`;
      }).join("");

      const ts = new Date().toLocaleString("en", { dateStyle: "short", timeStyle: "short" });
      const isUnpaid = order.payment_status !== "paid";
      const html = `<!doctype html><html><head><title>Receipt #${escape(order.order_number)}</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:'Courier New',ui-monospace,monospace;font-size:12px;line-height:1.4;padding:8px;width:80mm;max-width:80mm;color:#000}
          .ctr{text-align:center}
          h1{font-size:16px;font-weight:900}
          h2{font-size:13px;font-weight:bold;margin-top:6px}
          .meta{font-size:11px;margin:6px 0;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:4px 0}
          .meta .r{display:flex;justify-content:space-between}
          .row{padding:3px 0;border-bottom:1px dashed #ccc}
          .row:last-child{border-bottom:none}
          .ln{display:flex;justify-content:space-between;font-weight:bold}
          .ex{font-size:11px;padding-left:8px;color:#444}
          .tot{font-size:14px;font-weight:900;display:flex;justify-content:space-between;border-top:2px dashed #000;margin-top:6px;padding-top:6px}
          .status{margin-top:6px;text-align:center;font-weight:bold;padding:4px;border:2px solid #000}
          .status.paid{background:#16a34a;color:#fff;border-color:#16a34a}
          .status.unpaid{background:#fff3cd}
          .pmtbox{margin-top:8px;border-top:2px dashed #000;padding-top:6px}
          .pmtbox h2{margin-bottom:6px;text-align:center;font-size:18px;font-weight:900;letter-spacing:1px}
          .pay{padding:6px 0;border-bottom:1px dashed #ccc}
          .pay:last-child{border-bottom:none}
          .pn{display:flex;justify-content:space-between;font-weight:bold;font-size:15px}
          .ussd{font-family:'Courier New',monospace;font-size:18px;font-weight:900;text-align:center;padding:5px 3px;background:#000;color:#fff;margin-top:3px;letter-spacing:1px}
          .instr{font-size:11px;font-style:italic;margin-top:2px;color:#555}
          .ft{text-align:center;font-size:10px;margin-top:8px;color:#666}
          @page{size:80mm auto;margin:0}
        </style></head><body>
        <div class="ctr">
          <h1>${escape(restaurant?.name || "Receipt")}</h1>
          ${restaurant?.address ? `<div style="font-size:11px">${escape(restaurant.address)}</div>` : ""}
          ${restaurant?.contact_phone ? `<div style="font-size:11px">Tel: ${escape(restaurant.contact_phone)}</div>` : ""}
        </div>
        <div class="meta">
          <div class="r"><span>Order</span><b>#${escape(order.order_number)}</b></div>
          <div class="r"><span>Type</span><b>${order.order_type === "eat_in" ? `Table ${escape(order.table_number || "")}` : "Takeaway"}</b></div>
          <div class="r"><span>Date</span><b>${escape(ts)}</b></div>
          <div class="r"><span>Method</span><b>${escape((order.payment_method || "").toUpperCase().replace(/_/g, " "))}</b></div>
        </div>
        ${itemRows}
        <div class="tot"><span>TOTAL</span><span>${formatCurrency(amt)}</span></div>
        <div class="status ${isUnpaid ? "unpaid" : "paid"}">${isUnpaid ? "PAYMENT PENDING" : "PAID"}</div>
        ${isUnpaid && providerRows ? `<div class="pmtbox"><h2>PAYMENT OPTIONS</h2>${providerRows}<div style="font-size:11px;text-align:center;margin-top:6px"><b>Total to pay: ${formatCurrency(amt)}</b><br/>Replace <b>$</b> with the amount when dialing.</div></div>` : ""}
        <div class="ft">— Powered by Dalabsom —</div>
      </body></html>`;

      const existing = document.getElementById("dalabsom-customer-receipt-iframe");
      if (existing) existing.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "dalabsom-customer-receipt-iframe";
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      const doPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          console.error("iframe print failed, opening new tab:", err);
          const w = window.open("", "_blank");
          if (w) { w.document.open(); w.document.write(html); w.document.close(); }
          else { toast.error("Allow popups to print receipts"); }
        }
        setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 60000);
      };
      if (iframe.contentDocument.readyState === "complete") setTimeout(doPrint, 200);
      else iframe.onload = () => setTimeout(doPrint, 200);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't generate receipt");
    }
  };

  // Sound notification for status changes
  const playStatusSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      if (!audioContext.current) {
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContext.current;
      const now = ctx.currentTime;
      
      // Pleasant chime sound
      const playChime = (freq, time, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.2, time);
        gain.gain.exponentialRampToValueAtTime(0.01, time + dur);
        osc.start(time);
        osc.stop(time + dur);
      };
      
      // Two-tone notification
      playChime(523, now, 0.2);
      playChime(659, now + 0.15, 0.3);
    } catch (e) {
      console.error("Audio playback failed:", e);
    }
  }, [soundEnabled]);

  useEffect(() => {
    fetchOrder();
    
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchOrder, 10000);
    return () => {
      clearInterval(interval);
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/public/orders/${orderId}/track`);
      const newOrder = response.data.order;
      setRestaurant(response.data.restaurant);
      
      // Check if status changed and play sound
      if (previousStatus.current && previousStatus.current !== newOrder.status) {
        playStatusSound();
      }
      previousStatus.current = newOrder.status;
      
      setOrder(newOrder);
    } catch (err) {
      setError("Order not found");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStep = (status) => {
    const steps = { new: 0, preparing: 1, ready: 2, completed: 3 };
    return steps[status] ?? 0;
  };

  const statusSteps = [
    { status: "new", label: "Order Received", icon: CheckCircle, description: "Your order has been received" },
    { status: "preparing", label: "Preparing", icon: ChefHat, description: "Kitchen is preparing your order" },
    { status: "ready", label: "Ready", icon: Package, description: "Your order is ready for pickup/service" },
    { status: "completed", label: "Served", icon: UtensilsCrossed, description: "Enjoy your meal!" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex flex-col items-center justify-center p-4">
        <Package className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h1 className="text-xl font-bold mb-2">Order Not Found</h1>
        <p className="text-muted-foreground mb-4">We could not find this order</p>
        <Button onClick={() => window.history.back()}>Go Back</Button>
      </div>
    );
  }

  const currentStep = getStatusStep(order.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 p-4" data-testid="order-tracking">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => window.history.back()}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? "Mute notifications" : "Enable notifications"}
            >
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-sky-500" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchOrder}>
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Restaurant & Order Info */}
        <div className="text-center mb-8">
          {restaurant?.logo_data && (
            <img 
              src={restaurant.logo_data} 
              alt={restaurant.name}
              className="w-16 h-16 rounded-2xl object-cover mx-auto mb-3 shadow-md"
            />
          )}
          <h1 className="text-2xl font-bold mb-1">{restaurant?.name}</h1>
          <p className="text-muted-foreground">Order #{order.order_number}</p>
          <Badge className="mt-2" style={{ backgroundColor: restaurant?.brand_color }}>
            {order.order_type === "eat_in" ? `Table ${order.table_number}` : "Takeaway"}
          </Badge>
        </div>

        {/* Current Status Highlight */}
        <div className={cn(
          "mb-6 p-4 rounded-xl text-center",
          order.status === "new" && "bg-blue-100",
          order.status === "preparing" && "bg-amber-100",
          order.status === "ready" && "bg-green-100",
          order.status === "completed" && "bg-sky-100"
        )}>
          <p className="text-sm text-muted-foreground">Current Status</p>
          <p className={cn(
            "text-xl font-bold",
            order.status === "new" && "text-blue-700",
            order.status === "preparing" && "text-amber-700",
            order.status === "ready" && "text-green-700",
            order.status === "completed" && "text-sky-700"
          )}>
            {statusSteps.find(s => s.status === order.status)?.label || order.status}
          </p>
        </div>

        {/* Quick action: Call Waiter (eat-in only) — no Pay Now/dialer */}
        {order.order_type === "eat_in" && (
          <div className="mb-6">
            <Button
              variant={waiterCalled ? "outline" : "default"}
              disabled={callingWaiter || waiterCalled}
              onClick={handleCallWaiter}
              className={cn("w-full h-12 font-semibold", waiterCalled && "border-emerald-300 text-emerald-700")}
              data-testid="call-waiter-btn"
            >
              <BellRing className="w-4 h-4 mr-2" />
              {waiterCalled ? "Waiter on the way..." : callingWaiter ? "Calling..." : "Call Waiter"}
            </Button>
          </div>
        )}

        {/* Payment options panel — read-only (no dialer). Customer types real amount. */}
        {order.payment_status !== "paid" && activeProviders.length > 0 && (
          <Card className="mb-6 bg-sky-50 border-sky-200">
            <CardContent className="p-4">
              <p className="text-base font-bold text-sky-900 mb-2">Payment Options</p>
              <p className="text-xs text-sky-700 mb-3">
                Pay <span className="font-bold">{formatCurrency(order.total)}</span> using any provider below. Dial the code on your phone (replace <span className="font-mono font-bold">$</span> with the amount).
              </p>
              <div className="space-y-2">
                {activeProviders.map((p) => {
                  // Use literal "$" placeholder — customer fills in amount themselves.
                  let ussd = p.merchant_number;
                  if (p.ussd_code && p.ussd_code.includes("*")) {
                    ussd = p.ussd_code
                      .replace(/\{merchant\}/gi, p.merchant_number)
                      .replace(/\{amount\}/gi, "$")
                      .replace(/\{total\}/gi, "$");
                  }
                  return (
                    <div key={p.id || p.merchant_number || p.name}
                         className="block bg-white border border-sky-200 rounded p-2"
                         data-testid={`track-pay-${(p.name||'').toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="flex justify-between text-sm">
                        <span className="font-bold text-slate-900">{p.name}</span>
                        <span className="font-mono font-bold text-slate-900">{p.merchant_number}</span>
                      </div>
                      <p className="font-mono text-base font-bold text-slate-800 mt-1">{ussd}</p>
                      {p.instructions && <p className="text-[10px] text-slate-500 mt-0.5 italic">{p.instructions}</p>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Timeline */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="relative">
              {statusSteps.map((step, index) => {
                const isCompleted = index <= currentStep;
                const isCurrent = index === currentStep;
                
                return (
                  <div key={step.status} className="flex items-start gap-4 mb-6 last:mb-0">
                    {/* Icon & Line */}
                    <div className="relative">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                        isCompleted 
                          ? "bg-sky-500 text-white" 
                          : "bg-gray-200 text-gray-400"
                      )}>
                        <step.icon className="w-5 h-5" />
                      </div>
                      {index < statusSteps.length - 1 && (
                        <div className={cn(
                          "absolute left-5 top-10 w-0.5 h-12 -translate-x-1/2",
                          index < currentStep ? "bg-sky-500" : "bg-gray-200"
                        )} />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <h3 className={cn(
                        "font-semibold",
                        isCompleted ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {step.label}
                      </h3>
                      <p className={cn(
                        "text-sm",
                        isCurrent ? "text-sky-600" : "text-muted-foreground"
                      )}>
                        {step.description}
                      </p>
                      {isCurrent && order.status !== "completed" && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-sky-600">
                          <div className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
                          <span>In progress...</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Order Details</h3>
            <div className="space-y-2">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span>
                    {item.quantity}× {item.name}
                    {item.variant_name && <span className="text-muted-foreground"> ({item.variant_name})</span>}
                  </span>
                  <span>{formatCurrency(item.total)}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span className="text-sky-600">{formatCurrency(order.total)}</span>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full mt-3 h-10"
              onClick={handlePrintReceipt}
              data-testid="customer-print-receipt-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </Button>
          </CardContent>
        </Card>

        {/* Payment Status */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Payment Status</span>
              <Badge className={cn(
                order.payment_status === "paid" 
                  ? "bg-green-100 text-green-700" 
                  : "bg-amber-100 text-amber-700"
              )}>
                {order.payment_status === "paid" ? "Paid" : "Pending"}
              </Badge>
            </div>
            {["evc_plus", "zaad", "edahab"].includes(order.payment_method) && order.payment_status !== "paid" && (
              <div className="mt-3 p-3 bg-sky-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Phone className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-sky-800">Mobile Money Payment</p>
                    <p className="text-xs text-sky-700">Check your phone for payment prompt</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Estimated Time */}
        {order.status !== "completed" && order.status !== "ready" && (
          <div className="text-center text-sm text-muted-foreground">
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Estimated: 15-25 minutes</span>
            </div>
          </div>
        )}

        {order.status === "ready" && (
          <div className="text-center p-4 bg-green-100 rounded-xl">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="font-semibold text-green-800">Your order is ready!</p>
            <p className="text-sm text-green-700">
              {order.order_type === "eat_in" 
                ? "A server will bring it to your table shortly" 
                : "Please collect it from the counter"}
            </p>
          </div>
        )}

        {/* Sound notification info */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          {soundEnabled ? (
            <p>🔔 You will hear a notification when your order status changes</p>
          ) : (
            <p>🔇 Sound notifications are muted</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
